/**
 * Workflow prompt values may be plain text or structured JSON (object/array).
 * Stored and sent to automation as a string (JSON is compact-stringified).
 */

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** True when trimmed text parses as JSON object or array. */
export function isJsonPromptString(text: string): boolean {
  const t = text.trim();
  if (!t.startsWith('{') && !t.startsWith('[')) return false;
  try {
    const parsed: unknown = JSON.parse(t);
    return parsed !== null && typeof parsed === 'object';
  } catch {
    return false;
  }
}

/**
 * Normalize API/import input to the persisted prompt string.
 * - Objects/arrays → compact JSON
 * - JSON strings → validated and re-stringified (canonical form)
 * - Plain text → trimmed as-is
 */
export function normalizeWorkflowPrompt(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (isPlainObject(value) || Array.isArray(value)) {
    return JSON.stringify(value);
  }
  const text = typeof value === 'string' ? value.trim() : String(value).trim();
  if (!text) return '';
  if (isJsonPromptString(text)) {
    try {
      return JSON.stringify(JSON.parse(text) as unknown);
    } catch {
      return text;
    }
  }
  return text;
}

/** Extract prompt text from timeline scene/node payloads (string, JSON, or `{ prompt }`). */
export function extractWorkflowPrompt(value: unknown): string {
  if (typeof value === 'string') return value;
  if (isPlainObject(value) && typeof value.prompt === 'string') {
    return value.prompt;
  }
  if (isPlainObject(value) || Array.isArray(value)) {
    return normalizeWorkflowPrompt(value);
  }
  return '';
}

/** Pretty-print JSON prompts for textarea display; plain text unchanged. */
export function formatWorkflowPromptForDisplay(stored: string): string {
  const text = stored.trim();
  if (!text || !isJsonPromptString(text)) return stored;
  try {
    return JSON.stringify(JSON.parse(text) as unknown, null, 2);
  } catch {
    return stored;
  }
}

/** String passed to browser automation (Flow prompt editor). */
export function workflowPromptForAutomation(stored: string): string {
  return stored.trim();
}

/** Parse spreadsheet/import cell values (string or embedded object). */
export function workflowPromptFromImportCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (isPlainObject(value) || Array.isArray(value)) {
    return normalizeWorkflowPrompt(value);
  }
  return normalizeWorkflowPrompt(cellToString(value));
}

function cellToString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Number.isInteger(value) ? String(Math.trunc(value)) : String(value);
  }
  return String(value).trim();
}
